// wowhead.ts
// Shared helpers for Wowhead tooltips and item URLs.
//
// Extracted from SoftReservePage.tsx so multiple pages (BIS list editor,
// soft reserves, future loot history / equipment views) share one tooltip
// implementation rather than divergent copies.
//
// The Wowhead URL prefix differs per expansion ("", "tbc", "wotlk", "cata",
// "mop", "classic" for Classic Era / Anniversary). The original helper in
// SoftReservePage hardcoded "tbc"; this version accepts a game code and
// maps it to the right segment, falling back to "tbc" when no code is
// provided so existing call sites continue to work unchanged.

const GAME_CODE_TO_WOWHEAD_SEGMENT: Record<string, string> = {
  // Empty string means retail (no segment in the URL).
  // wow_classic_anniv maps to "tbc" because the seeded loot rows in
  // raid_loot_item are TBC-era items (Karazhan, Gruul's, etc.) — same
  // segment SoftReservePage.tsx originally hardcoded and was working.
  wow_classic_anniv: "tbc",
  wow_classic_era: "classic",
  wow_tbc: "tbc",
  wow_wotlk: "wotlk",
  wow_cata: "cata",
  wow_mop: "mop",
  wow_retail: "",
};

export function gameCodeToWowheadSegment(gameCode?: string | null): string {
  if (!gameCode) return "tbc";
  // If we don't recognise the code, fall back to "tbc" — this preserves
  // SoftReservePage's pre-extraction behaviour for any unknown game.
  const seg = GAME_CODE_TO_WOWHEAD_SEGMENT[gameCode];
  return seg === undefined ? "tbc" : seg;
}

export function wowheadItemUrl(
  externalItemId: string | number,
  gameCode?: string | null
): string {
  const segment = gameCodeToWowheadSegment(gameCode);
  const base = "https://www.wowhead.com";
  return segment ? `${base}/${segment}/item=${externalItemId}` : `${base}/item=${externalItemId}`;
}

/**
 * Build the Wowhead `pcs` query value — colon-separated item IDs the player
 * owns/equipped. Wowhead highlights matching set pieces in the tooltip and
 * updates the set counter (e.g. Warbringer Battlegear (2/5)).
 *
 * Loot council only today; safe to stop calling if we need to revert.
 */
export function wowheadSetPiecesParam(
  ownedItemIds: Iterable<string | number>
): string | null {
  const ids = Array.from(
    new Set(
      Array.from(ownedItemIds)
        .map((id) => String(id).trim())
        .filter((id) => id.length > 0 && /^\d+$/.test(id))
    )
  );
  return ids.length > 0 ? ids.join(":") : null;
}

/** Item URL with optional set-piece ownership for per-character tooltips. */
export function wowheadItemUrlWithSetPieces(
  externalItemId: string | number,
  gameCode?: string | null,
  ownedSetPieceIds?: Iterable<string | number> | null
): string {
  const base = wowheadItemUrl(externalItemId, gameCode);
  const pcs = ownedSetPieceIds ? wowheadSetPiecesParam(ownedSetPieceIds) : null;
  return pcs ? `${base}?pcs=${pcs}` : base;
}

// Trigger Wowhead's tooltip script to scan the DOM for newly added <a> tags.
// Safe to call before the script has loaded (no-op in that case).
export function refreshWowheadTooltips(): void {
  if (typeof window !== "undefined" && typeof (window as any).$WowheadPower !== "undefined") {
    (window as any).$WowheadPower.refreshLinks();
  }
}
